import { Component, inject, signal } from '@angular/core';
import { FormBuilder, Validators, AbstractControl, ReactiveFormsModule } from '@angular/forms';
import {
  MatDialogRef,
  MatDialogActions,
  MatDialogContent,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { InputField } from '../../../../shared/components/input-field/input-field';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  debounceTime,
  map,
  distinctUntilChanged,
  switchMap,
  catchError,
  of,
  shareReplay,
  filter,
} from 'rxjs';
import { WebsitesService } from '../../../../core/services/websites.service';
import { AsyncPipe } from '@angular/common';

@Component({
  selector: 'app-import-ssl-certificate-global-dialog',
  imports: [
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    InputField,
    AsyncPipe,
  ],
  templateUrl: './import-ssl-certificate-global-dialog.component.html',
  styleUrl: './import-ssl-certificate-global-dialog.component.scss',
})
export class ImportSslCertificateGlobalDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<ImportSslCertificateGlobalDialogComponent>);

  private readonly fb = inject(FormBuilder);

  private readonly websitesService = inject(WebsitesService);

  readonly domainSearchText = signal('');

  private readonly domainSearchText$ = toObservable(this.domainSearchText);

  readonly websiteOptions$ = this.domainSearchText$.pipe(
    debounceTime(100),
    map((searchText) => searchText.trim()),
    distinctUntilChanged(),
    filter((domainSearch) => domainSearch.length >= 1),
    switchMap((domainSearch) =>
      this.websitesService.searchWebsites({
        page: 1,
        pageSize: 20,
        domainSearch: domainSearch || undefined,
      }),
    ),
    map(({ Websites }) =>
      Websites.map((website) => ({
        label: website.domain,
        value: String(website.id),
      })),
    ),
    catchError(() => of([])),
    shareReplay({
      bufferSize: 1,
      refCount: true,
    }),
  );

  readonly VALIDATORS = {
    certificate: [
      (control: AbstractControl) => {
        if (!control.value) {
          return { required: 'Certificate is required' };
        }

        if (!control.value.startsWith('-----BEGIN CERTIFICATE-----')) {
          return { certificateError: 'Certificate is invalid' };
        }

        if (!control.value.endsWith('-----END CERTIFICATE-----\n')) {
          return { certificateError: 'Certificate is invalid' };
        }

        return null;
      },
    ],

    ca: [
      (control: AbstractControl) => {
        if (!control.value) {
          return null;
        }

        if (!control.value.startsWith('-----BEGIN CERTIFICATE-----')) {
          return { caBundleError: 'CA Bundle is invalid' };
        }

        if (!control.value.endsWith('-----END CERTIFICATE-----\n')) {
          return { caBundleError: 'CA Bundle is invalid' };
        }

        return null;
      },
    ],

    key: [
      (control: AbstractControl) => {
        if (!control.value) {
          return { required: 'Key is required' };
        }

        if (!control.value.startsWith('-----BEGIN PRIVATE KEY-----')) {
          return { keyError: 'Private Key is invalid' };
        }

        if (!control.value.endsWith('-----END PRIVATE KEY-----\n')) {
          return { keyError: 'Private Key is invalid' };
        }

        return null;
      },
    ],
  };

  readonly ERROR_MESSAGES: Record<string, Record<string, string>> = {
    websiteId: {
      required: 'Domain is required',
    },
    certificate: {
      required: 'Certificate is required',
      certificateError: 'Certificate is invalid',
    },
    key: {
      required: 'Key is required',
      keyError: 'Private Key is invalid',
    },
    ca: {
      caBundleError: 'CA Bundle is invalid',
    },
  };

  readonly form = this.fb.nonNullable.group({
    websiteId: ['', Validators.required],
    certificate: ['', this.VALIDATORS.certificate],
    key: ['', this.VALIDATORS.key],
    ca: ['', this.VALIDATORS.ca],
  });

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.getRawValue();

    this.dialogRef.close({
      ...value,
      websiteId: Number(value.websiteId),
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
